// src/modules/LocationMovements/adapters/LocationMovementsApiAdapter.ts

import { HttpClient } from '@adapters/HttpClient';
import type { MovementBatchSummary } from '@modules/Reports/models/MovementBatch';
import { buildMovementAdvancedFiltersParams } from '@modules/Reports/utils/buildMovementAdvancedFiltersParams';
import { Filters } from '../models/Filters';
import {
  LocationMovementsModel,
  LocationMovementApiResponse,
  MovementEntity,
  MovementEntityType,
} from '../models/LocationMovements';

function mapBatch(
  batch: MovementBatchSummary | null | undefined,
  batchId: number | null | undefined,
): { batch_id: number | null; batch: MovementBatchSummary | null } {
  if (!batch && (batchId == null || batchId === undefined)) {
    return { batch_id: null, batch: null };
  }
  if (batch?.id) {
    return {
      batch_id: batchId ?? batch.id,
      batch: {
        id: batch.id,
        batch_number: batch.batch_number,
        expiry_date: batch.expiry_date ?? null,
        received_at: batch.received_at ?? null,
        supplier_reference: batch.supplier_reference ?? null,
      },
    };
  }
  if (batchId != null) {
    return { batch_id: batchId, batch: null };
  }
  return { batch_id: null, batch: null };
}

function mapEntity(
  obj: MovementEntity | null | undefined,
  fallbackId: number | null | undefined,
  type: MovementEntityType,
): MovementEntity | null {
  if (obj?.id) return { ...obj, type: obj.type ?? type };
  if (fallbackId) return { type, id: fallbackId };
  return null;
}

/** Mapea la respuesta del API al modelo del frontend */
function mapApiResponseToModel(item: LocationMovementApiResponse): LocationMovementsModel {
  const orderWithShop = item.order as
    | { id: number; ref: string; shop?: { id: number; name: string; image?: string } }
    | undefined;
  const shop = orderWithShop?.shop ? { name: orderWithShop.shop.name, image: orderWithShop.shop.image ?? '' } : null;

  return {
    id: item.id,
    product_id: item.product_id,
    order_id: item.order_id ?? null,
    order: item.order ? { id: item.order.id, ref: item.order.ref } : null,
    from_location_id: item.source_location_id,
    to_location_id: item.target_location_id,
    from_type_id: item.source_type_id,
    to_type_id: item.target_type_id,
    qty: item.quantity,
    team_id: item.team_id ?? null,
    stock_location_order_id: item.stock_location_order_id ?? null,
    returned_reception_id: item.returned_reception_id ?? null,
    entry_id: item.entry_id ?? null,
    extra_work_order_id: item.extra_work_order_id ?? null,
    location_order_id: item.location_order_id ?? null,
    description: item.description ?? null,
    merchandise_id: null,
    merchandise: null,
    product: {
      id: item.product.id,
      name: item.product.name ?? item.product.code ?? '',
      code: item.product.code ?? '',
      image: item.product.image ?? item.product.img,
    },
    from_location: item.source_location ?? null,
    to_location: item.target_location ?? null,
    from_type: item.source_type ?? null,
    to_type: item.target_type ?? null,
    event: {
      id: 0,
      name: item.event_type,
    },
    available_stock_after_event: item.total,
    total: item.total,
    user: item.user ?? null,
    shop,
    team: item.team ?? null,
    stock_location_order: mapEntity(item.stock_location_order, item.stock_location_order_id, 'preparation'),
    returned_reception: mapEntity(item.returned_reception, item.returned_reception_id, 'returned_reception'),
    location_order: mapEntity(item.location_order, item.location_order_id, 'location_order'),
    entry: mapEntity(item.entry, item.entry_id, 'entry'),
    extra_work_order: mapEntity(item.extra_work_order, item.extra_work_order_id, 'extra_work_order'),
    ...mapBatch(item.batch, item.batch_id),
    created_at: item.created_at,
  };
}

export interface StockMovementPage {
  stockmovements: LocationMovementsModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

// 🎯 Centralización de filtros por defecto
const DEFAULT_FILTERS = {
  sortBy: 'id',
  sortDirection: 'desc' as 'asc' | 'desc',
  page: 1,
  limit: 15,
};

export class LocationMovementsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * 🔄 Obtiene los movimientos paginados desde /stock-movement
   */
  async getLocationMovementsPaginated(filters: Filters = {}): Promise<StockMovementPage> {
    const {
      searchTerm = '',
      type_ids,
      product_ids,
      from_location_id,
      to_location_id,
      sortBy = DEFAULT_FILTERS.sortBy,
      sortDirection = DEFAULT_FILTERS.sortDirection,
      page = DEFAULT_FILTERS.page,
      limit = DEFAULT_FILTERS.limit,
      advanced_filters,
    } = filters;

    // 🧠 Serializar filtros a formato backend
    const params: Record<string, any> = {
      // Parámetro para mostrar solo movimientos entre ubicaciones (1/0 para compatibilidad con validación Laravel)
      only_location_movements: 1,
      ...(searchTerm && { search: searchTerm }),
      ...(type_ids && {
        type_ids: Array.isArray(type_ids) ? type_ids.join(',') : type_ids,
      }),
      ...(product_ids && {
        product_ids: Array.isArray(product_ids) ? product_ids.join(',') : product_ids,
      }),
      ...(from_location_id && {
        from_location_id: Array.isArray(from_location_id) ? from_location_id.join(',') : from_location_id,
      }),
      ...(to_location_id && {
        to_location_id: Array.isArray(to_location_id) ? to_location_id.join(',') : to_location_id,
      }),
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // 🎯 Agregar advanced_filters solo si el usuario los proporciona explícitamente
    if (advanced_filters && advanced_filters.filters?.length) {
      const advancedParams = buildMovementAdvancedFiltersParams(advanced_filters);
      Object.assign(params, advancedParams);
    }

    // 📡 Petición GET al endpoint
    const response = await this.httpClient.get<any>('/stock-movement', params);

    const rawData: LocationMovementApiResponse[] = Array.isArray(response.data)
      ? response.data
      : (response.data?.data ?? []);
    const stockmovements = rawData.map((item) => mapApiResponseToModel(item));

    // 🔁 Estructura para scroll infinito
    return {
      stockmovements,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  /**
   * 🧾 Trae todos los movimientos sin paginación (limitado internamente)
   */
  async getAll(filters: Filters = {}): Promise<LocationMovementsModel[]> {
    const response = await this.getLocationMovementsPaginated({
      ...filters,
      limit: 1000, // Límite alto para obtener todos
    });
    return response.stockmovements;
  }

  /**
   * 📊 Exporta movimientos a Excel
   */
  async exportToExcel(filters: Filters = {}): Promise<string> {
    const {
      searchTerm = '',
      type_ids,
      product_ids,
      from_location_id,
      to_location_id,
      sortBy = DEFAULT_FILTERS.sortBy,
      sortDirection = DEFAULT_FILTERS.sortDirection,
      page = DEFAULT_FILTERS.page,
      limit = DEFAULT_FILTERS.limit,
      advanced_filters,
    } = filters;

    const params: Record<string, any> = {
      // Parámetro para mostrar solo movimientos entre ubicaciones (1/0 para compatibilidad con validación Laravel)
      only_location_movements: 1,
      ...(searchTerm && { search: searchTerm }),
      ...(type_ids
        ? {
            type_ids: Array.isArray(type_ids) ? type_ids.join(',') : type_ids,
          }
        : {}),
      ...(product_ids
        ? {
            product_ids: Array.isArray(product_ids) ? product_ids.join(',') : product_ids,
          }
        : {}),
      ...(from_location_id
        ? {
            from_location_id: Array.isArray(from_location_id) ? from_location_id.join(',') : from_location_id,
          }
        : {}),
      ...(to_location_id
        ? {
            to_location_id: Array.isArray(to_location_id) ? to_location_id.join(',') : to_location_id,
          }
        : {}),
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // 🎯 Agregar advanced_filters solo si el usuario los proporciona explícitamente
    if (advanced_filters && advanced_filters.filters?.length) {
      const advancedParams = buildMovementAdvancedFiltersParams(advanced_filters);
      Object.assign(params, advancedParams);
    }

    const response = await this.httpClient.post<any>('/stock-movement/export', params);

    // 🎯 La API devuelve { url_file: "https://..." } directamente
    if (!response?.url_file) {
      throw new Error('La respuesta de exportación no contiene url_file');
    }

    return response.url_file;
  }
}
